import {
  isAdminCheckoutOptimizationUser,
  isTrustedAdminCheckoutOptimizationUser,
} from "@redotech/redo-conversion-core-common/checkouts/trusted-users";
import {
  CoverageMlModelId,
  INTENT_V12_28_2025_COVERAGE_ML_MODEL_ID,
  STAGECOACH_COVERAGE_ML_MODEL_ID,
} from "@redotech/redo-coverage-common/coverage-ml/types";
import { PricingRuleSet } from "@redotech/redo-return-coverage-common/coverage-types";
import {
  groupInput,
  GroupInput,
  Input,
  input,
  InputSchema,
  listInput,
} from "@redotech/ui/form";
import { ValidationError, Validator } from "@redotech/ui/validator";
import { numberValidator } from "@redotech/ui/validators";
import { arrayEqual, stringEqual } from "@redotech/util/equal";
import classNames from "classnames";
import round from "lodash/round";
import { memo, ReactNode, useEffect, useMemo, useState } from "react";
import { Divider } from "../arbiter-components/divider/divider";
import { TextRedoListItem } from "../arbiter-components/list/redo-list";
import { RedoSingleSelectDropdownInput } from "../arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { RedoToggle } from "../arbiter-components/toggle/redo-toggle";
import { DisabledTooltip } from "../arbiter-components/tooltip/disabled-tooltip";
import { Button, ButtonBorder, ButtonTheme, IconButton } from "../button";
import { Flex } from "../flex";
import * as gridCss from "../grid.module.css";
import PlusIcon from "../icon-old/plus.svg";
import RightArrowIcon from "../icon-old/right-arrow.svg";
import TrashIcon from "../icon-old/trash.svg";
import { LabelTheme } from "../labeled-input";
import {
  CountryFormMultiSelectDropdown,
  FormSelectDropdown,
} from "../select-dropdown";
import { SettingSectionWrapper } from "../setting-wrapper/setting-section-wrapper";
import { SettingWrapper } from "../setting-wrapper/setting-wrapper";
import { Tabs } from "../tab";
import { FormTextInput } from "../text-input";
import * as pricingCss from "./pricing.module.css";

const priceBracketForm = groupInput({
  value: input<string>({ validator: numberValidator({ min: 0.01 }) }),
  pricePoint: input<string>(),
});

const priceRuleDefault = (
  index: number,
): InputSchema.Value<typeof priceBracketForm> => ({
  value: "2",
  pricePoint: "0",
});

export const ascendingValuesValidator: Validator<PricingBracketValue[]> = (
  value,
) => {
  const errors: ValidationError[] = [];

  for (let i = 0; i < value.length; i++) {
    if (i === value.length - 1) {
      break;
    }
    // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
    if (Number(value[i].pricePoint) > Number(value[i + 1].pricePoint)) {
      errors.push(`${i}`);
    }
  }
  return errors;
};

export const percentageValidator: Validator<any> = (
  value: PricingRuleSetValue,
) => {
  const errors: ValidationError[] = [];

  if (value.type === "percentage") {
    for (let i = 0; i < value.priceBrackets.length; i++) {
      // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
      if (+value.priceBrackets[i].value > 100) {
        errors.push(`${i}`);
      }
    }
  }

  return errors;
};

export const pricingRuleSetForm = groupInput(
  {
    type: input<"fixed" | "percentage">(),
    countries: input<string[]>({ equal: arrayEqual(stringEqual) }),
    priceBrackets: listInput(
      () => priceBracketForm,
      priceRuleDefault,
      (_, index) => index,
      { validator: ascendingValuesValidator },
    ),
  },
  { validator: percentageValidator },
);

export type PricingRuleSetInput = InputSchema.Value<typeof pricingRuleSetForm>;

export const pricingRuleSetDefault = (index: number): PricingRuleSetInput => ({
  type: "percentage",
  countries: [],
  priceBrackets: [{ value: "2", pricePoint: "0" }],
});

export const minMaxValidator: Validator<any> = (value: PricingFormValue) => {
  const errors: ValidationError[] = [];
  for (let i = 0; i < value.pricingRuleSet.length; i++) {
    // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
    if (value.pricingRuleSet[i].type === "percentage") {
      continue;
    }

    // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
    for (let j = 0; j < value.pricingRuleSet[i].priceBrackets.length; j++) {
      // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
      if (!value.pricingRuleSet[i].priceBrackets[j].value.length) {
        continue;
      }

      if (
        // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        Number(value.pricingRuleSet[i].priceBrackets[j].value) <
          Number(value.minPrice) ||
        // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        Number(value.pricingRuleSet[i].priceBrackets[j].value) >
          Number(value.maxPrice)
      ) {
        errors.push(`${i},${j}`);
      }
    }
  }
  return errors;
};

export const pricingFormValues = {
  minPrice: input<string>({ validator: numberValidator({ min: 0.01 }) }),
  maxPrice: input<string>({ validator: numberValidator({ min: 0.01 }) }),
  percentage: input<string>({
    validator: numberValidator({ min: 0.01, max: 100 }),
  }),
  internationalPricingEnabled: input<boolean>(),
  pricingRuleSet: listInput(
    () => pricingRuleSetForm,
    pricingRuleSetDefault,
    (_, index) => index,
  ),
};

export const pricingForm = groupInput(pricingFormValues, {
  validator: minMaxValidator,
});

export const pricingDefault: InputSchema.Value<typeof pricingForm> = {
  minPrice: "1",
  maxPrice: "15",
  percentage: "2",
  internationalPricingEnabled: false,
  pricingRuleSet: [
    {
      type: "percentage",
      countries: [],
      priceBrackets: [{ value: "2", pricePoint: "0" }],
    },
  ],
};

export type PricingForm = InputSchema.Input<typeof pricingForm>;
export type PricingRuleSetForm = InputSchema.Input<typeof pricingRuleSetForm>;
type PricingFormValue = InputSchema.Value<typeof pricingForm>;
type PricingRuleSetValue = InputSchema.Value<typeof pricingRuleSetForm>;
type PricingBracketValue = InputSchema.Value<typeof priceBracketForm>;

/** Appends a tier one unit above the last, $100 further up the cart. */
export function addPriceTier(pricingRuleSet: PricingRuleSetForm): void {
  const brackets = pricingRuleSet.inputs.priceBrackets.value;
  const last = brackets.at(-1);
  pricingRuleSet.inputs.priceBrackets.setValue([
    ...brackets,
    {
      value: String(Number(last?.value ?? 0) + 1),
      pricePoint: String(Number(last?.pricePoint ?? 0) + 100),
    },
  ]);
}

/** Merges tier `index` into the next, which keeps its start and takes this tier's price. */
export function removePriceTier(
  pricingRuleSet: PricingRuleSetForm,
  index: number,
): void {
  const brackets = pricingRuleSet.inputs.priceBrackets.value;
  const next = brackets[index + 1];
  if (!next) {
    return;
  }
  pricingRuleSet.inputs.priceBrackets.setValue(
    brackets.flatMap((bracket, i) => {
      if (i === index) {
        return [{ ...bracket, value: next.value }];
      }
      return i === index + 1 ? [] : [bracket];
    }),
  );
}

export function priceTierErrors({
  pricingRuleSet,
  ruleSetIndex,
  pricingErrors,
  tierIndex,
}: {
  pricingRuleSet: PricingRuleSetForm;
  ruleSetIndex: number;
  /** The pricing group's own errors, where the min/max check reports `rule,tier`. */
  pricingErrors: readonly string[];
  tierIndex: number;
}): {
  outsideMinMax: boolean;
  overHundredPercent: boolean;
  notAscending: boolean;
} {
  return {
    outsideMinMax: pricingErrors.includes(`${ruleSetIndex},${tierIndex}`),
    overHundredPercent: pricingRuleSet.errors.includes(`${tierIndex}`),
    notAscending: pricingRuleSet.inputs.priceBrackets.errors.includes(
      `${tierIndex + 1}`,
    ),
  };
}

export const PriceBracketControls = memo(function PriceBracketControls({
  pricingRuleSet,
  minMaxErrors,
  index,
  canEditSettings = true,
  adminApp = false,
  currencyDisplay,
}: {
  pricingRuleSet: PricingRuleSetForm;
  minMaxErrors: string[];
  index: number;
  canEditSettings: boolean;
  adminApp?: boolean;
  currencyDisplay?: string;
}) {
  const priceBrackets = pricingRuleSet.inputs.priceBrackets;
  const elements = [];
  let isLast = false;

  for (let i = 0; i < priceBrackets.inputs.length; i++) {
    // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
    const fromValue = priceBrackets.inputs[i].value.pricePoint;
    let toInput;

    if (i === priceBrackets.inputs.length - 1) {
      // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
      toInput = priceBrackets.inputs[i].inputs.pricePoint;
      isLast = true;
    } else {
      // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
      toInput = priceBrackets.inputs[i + 1].inputs.pricePoint;
    }

    const {
      outsideMinMax: minMaxError,
      overHundredPercent: highPercentError,
      notAscending: priceBracketError,
    } = priceTierErrors({
      pricingRuleSet,
      ruleSetIndex: index,
      pricingErrors: minMaxErrors,
      tierIndex: i,
    });

    elements.push(
      <div className={gridCss.grid} key={i}>
        <span
          className={classNames(
            pricingCss.monospace,
            gridCss.span3,
            pricingCss.centered,
          )}
        >{`$ ${
          i === 0
            ? Number(fromValue).toFixed(2)
            : (Number(fromValue) + 0.01).toFixed(2)
        }`}</span>

        <span
          className={classNames(
            pricingCss.monospace,
            gridCss.span1,
            pricingCss.centered,
          )}
        >
          -
        </span>
        {isLast && (
          <span
            className={classNames(
              pricingCss.monospace,
              gridCss.span3,
              pricingCss.centered,
            )}
          >
            No limit
          </span>
        )}
        {!isLast && (
          <div
            className={classNames(
              pricingCss.monospace,
              gridCss.span3,
              pricingCss.errorOutline,
            )}
          >
            <FormTextInput
              description={
                priceBracketError ? (
                  <span className={pricingCss.errorText}>
                    Must be less than next row
                  </span>
                ) : (
                  ""
                )
              }
              errorOverride={priceBracketError}
              input={toInput}
              label=""
              labelTheme={LabelTheme.THIN}
              min={0.01}
              prefix={currencyDisplay}
              readonly={!canEditSettings}
              step={0.01}
            />
          </div>
        )}

        <RightArrowIcon
          className={classNames(pricingCss.icon, pricingCss.centered)}
        />

        <div className={classNames(pricingCss.monospace, gridCss.span3)}>
          <FormTextInput
            description={
              minMaxError ? (
                <span className={pricingCss.errorText}>
                  Value is outside of {adminApp ? <>your</> : <>the</>} min or
                  max
                </span>
              ) : highPercentError ? (
                <span className={pricingCss.errorText}>
                  Percentage can't exceed 100
                </span>
              ) : (
                ""
              )
            }
            errorOverride={minMaxError || highPercentError}
            // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
            input={priceBrackets.inputs[i].inputs.value}
            label=""
            labelTheme={LabelTheme.THIN}
            max={100}
            min={0.01}
            prefix={
              pricingRuleSet.value.type === "percentage" ? "" : currencyDisplay
            }
            readonly={!canEditSettings}
            step={0.01}
            suffix={pricingRuleSet.value.type === "percentage" ? "%" : ""}
          />
        </div>

        {!isLast && canEditSettings && (
          <IconButton onClick={() => removePriceTier(pricingRuleSet, i)}>
            <div className={pricingCss.iconButtonContent}>
              <TrashIcon />
            </div>
          </IconButton>
        )}
        {isLast && canEditSettings && (
          <IconButton onClick={() => addPriceTier(pricingRuleSet)}>
            <div className={pricingCss.iconButtonContent}>
              <PlusIcon />
            </div>
          </IconButton>
        )}
      </div>,
    );
  }

  return (
    <div>
      <div className={gridCss.grid}>
        <span className={classNames(gridCss.span3, pricingCss.centered)}>
          From
        </span>
        <span className={classNames(gridCss.span5, pricingCss.centered)}>
          To
        </span>
        <div className={gridCss.span4}>
          <FormSelectDropdown
            description=""
            disabled={!canEditSettings}
            input={pricingRuleSet.inputs.type}
            label=""
            options={["percentage", "fixed"]}
            placeholder="Default"
          >
            {pricingTypeLabel}
          </FormSelectDropdown>
        </div>
      </div>

      {elements}

      {canEditSettings && (
        <div
          className={classNames(
            gridCss.span12,
            pricingCss.centeredFlex,
            pricingCss.padded,
          )}
        >
          <Button
            border={ButtonBorder.LIGHT}
            className={classNames(pricingCss.centeredFlex)}
            onClick={() => addPriceTier(pricingRuleSet)}
            theme={ButtonTheme.OUTLINED}
          >
            <div className={pricingCss.buttonContent}>
              <PlusIcon className={pricingCss.icon} />
              Add new tier
            </div>
          </Button>
        </div>
      )}
    </div>
  );
});

/** Keeps a second, international rule set exactly while international pricing is on. */
export function useInternationalPricingRuleSet({
  inputs: { pricingRuleSet, percentage, internationalPricingEnabled },
}: {
  inputs: Pick<
    PricingForm["inputs"],
    "pricingRuleSet" | "percentage" | "internationalPricingEnabled"
  >;
}): void {
  useEffect(() => {
    if (
      pricingRuleSet.value.length === 2 &&
      !internationalPricingEnabled.value
    ) {
      pricingRuleSet.setValue(pricingRuleSet.value.slice(0, 1));
    } else if (
      internationalPricingEnabled.value &&
      pricingRuleSet.value.length === 1
    ) {
      pricingRuleSet.setValue([
        ...pricingRuleSet.value,
        {
          type: "percentage",
          countries: [],
          priceBrackets: [
            {
              value: percentage.value
                ? String(round(Number(percentage.value), 3))
                : "2",
              pricePoint: "0",
            },
          ],
        },
      ]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [internationalPricingEnabled.value]);
}

export const Pricing = memo(function Pricing({
  input,
  canEditSettings,
  adminApp = false,
  currencyDisplay,
  minPriceFooter,
  maxPriceFooter,
  maxPriceInput,
  maxPriceDisabled,
}: {
  input: PricingForm;
  canEditSettings: boolean;
  adminApp?: boolean;
  currencyDisplay?: string;
  minPriceFooter?: ReactNode;
  maxPriceFooter?: ReactNode;
  maxPriceInput: Input<string>;
  maxPriceDisabled?: boolean;
}) {
  const { minPrice, pricingRuleSet, internationalPricingEnabled } =
    input.inputs;
  useInternationalPricingRuleSet(input);

  const [property, setProperty] = useState(CountrySet.DOMESTIC);

  return (
    <div className={gridCss.grid}>
      <MinMaxPriceInput
        canEditSettings={canEditSettings}
        currencyDisplay={currencyDisplay}
        maxPriceDisabled={maxPriceDisabled}
        maxPriceFooter={maxPriceFooter}
        maxPriceInput={maxPriceInput}
        minPriceFooter={minPriceFooter}
        minPriceInput={minPrice}
      />

      <div className={classNames(gridCss.span12)}>
        <Tabs<CountrySet>
          keyFn={(option) => option}
          options={[CountrySet.DOMESTIC, CountrySet.INTERNATIONAL]}
          tab={(option) => CountrySetLables[option]}
          value={property}
          valueChange={setProperty}
        >
          {property === CountrySet.DOMESTIC ? (
            <div className={classNames(gridCss.span12)}>
              <SettingWrapper compact id="domestic-countries">
                <CountryFormMultiSelectDropdown
                  disabled={!canEditSettings}
                  emptyLabel="All"
                  // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
                  input={pricingRuleSet.inputs[0].inputs.countries}
                  label={`Select ${adminApp ? "" : "your "}domestic countries`}
                />
              </SettingWrapper>
              <div className={classNames(gridCss.span12, pricingCss.padded)}>
                <Divider />
              </div>

              <SettingWrapper compact id="price-brackets">
                <PriceBracketControls
                  adminApp={adminApp}
                  canEditSettings={canEditSettings}
                  currencyDisplay={currencyDisplay}
                  index={0}
                  minMaxErrors={input.errors}
                  // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
                  pricingRuleSet={pricingRuleSet.inputs[0]}
                />
              </SettingWrapper>
            </div>
          ) : (
            <div className={gridCss.span12}>
              <SettingWrapper compact id="international-pricing">
                <RedoToggle
                  description={`Enables coverage for all countries outside of ${
                    adminApp ? "the merchant's" : "your"
                  } domestic countries`}
                  disabled={!canEditSettings}
                  label="Enable coverage internationally"
                  setValue={internationalPricingEnabled.setValue}
                  size="sm"
                  value={internationalPricingEnabled.value}
                />
              </SettingWrapper>
              {pricingRuleSet.inputs.length > 1 &&
                internationalPricingEnabled.value && (
                  <>
                    <div
                      className={classNames(gridCss.span12, pricingCss.padded)}
                    >
                      <Divider />
                    </div>

                    <div className={classNames(gridCss.span12)}>
                      <SettingWrapper compact id="international-price-brackets">
                        <PriceBracketControls
                          adminApp={adminApp}
                          canEditSettings={canEditSettings}
                          index={1}
                          minMaxErrors={input.errors}
                          // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
                          pricingRuleSet={pricingRuleSet.inputs[1]}
                        />
                      </SettingWrapper>
                    </div>
                  </>
                )}
            </div>
          )}
        </Tabs>
      </div>
    </div>
  );
});

export enum CountrySet {
  DOMESTIC = "Domestic",
  INTERNATIONAL = "International",
}

export const CountrySetLables = {
  [CountrySet.DOMESTIC]: <>Domestic</>,
  [CountrySet.INTERNATIONAL]: <>International</>,
};

export function pricingTypeLabel(input: "percentage" | "fixed") {
  switch (input) {
    case "percentage":
      return "Percentage";
    case "fixed":
      return "Fixed amount";
  }
}

export function pricingRulesConvertTeamToValue(
  pricingRuleSet: PricingRuleSet[] | undefined,
  defaultCountries: string[] = [],
  defaultPercentage?: number,
): PricingRuleSetValue[] {
  let pricingRuleSetReturn: PricingRuleSetValue[] = [];
  if (pricingRuleSet?.length) {
    pricingRuleSetReturn = pricingRuleSet.map((x) => {
      return {
        type: x.type,
        countries: x.countries,
        priceBrackets: x.priceBrackets.map((pb) => {
          let value = pb.value;
          if (x.type === "percentage") {
            value = round(pb.value * 100, 3);
          }
          return { value: String(value), pricePoint: String(pb.pricePoint) };
        }),
      };
    });
  } else if (!pricingRuleSetReturn.length) {
    pricingRuleSetReturn.push({
      type: "percentage",
      countries: defaultCountries,
      priceBrackets: [
        {
          value: defaultPercentage
            ? String(round(defaultPercentage * 100, 3))
            : "2",
          pricePoint: "0",
        },
      ],
    });
  }

  return pricingRuleSetReturn;
}

export function pricingRulesConvertValueToTeam(
  pricingRuleSets: PricingRuleSetValue[] | undefined,
) {
  let teamReturn;
  if (pricingRuleSets) {
    teamReturn = pricingRuleSets.map((x) => {
      return {
        type: x.type,
        countries: x.countries,
        priceBrackets: x.priceBrackets.map((pb) => {
          let newValue = Number(pb.value);
          if (x.type === "percentage") {
            newValue = round(newValue / 100.0, 3);
          }
          return { value: newValue, pricePoint: Number(pb.pricePoint) };
        }),
      };
    });

    return teamReturn;
  } else {
    return undefined;
  }
}

export const MinMaxPriceInput = memo(function MinMaxPriceInput({
  minPriceInput,
  maxPriceInput,
  canEditSettings,
  currencyDisplay,
  minPriceFooter,
  maxPriceFooter,
  maxPriceDisabled = false,
}: {
  minPriceInput: Input<string>;
  maxPriceInput: Input<string>;
  canEditSettings: boolean;
  currencyDisplay?: string;
  // Optional slots rendered directly under the Min/Max price inputs so
  // per-coverage hints (PP+ min-price requirement, PP+ max-cart-value cap)
  // sit visually paired with the field they describe.
  minPriceFooter?: ReactNode;
  maxPriceFooter?: ReactNode;
  // Read-only max price for surfaces where it's derived (PP+).
  maxPriceDisabled?: boolean;
}) {
  return (
    <>
      <div className={classNames(gridCss.span6L, pricingCss.monospace)}>
        <SettingWrapper id="min-price">
          <FormTextInput
            disabled={!canEditSettings}
            input={minPriceInput}
            label="Min price"
            prefix={currencyDisplay}
            type="number"
          />
        </SettingWrapper>
        {minPriceFooter}
      </div>
      <div className={classNames(gridCss.span6L, pricingCss.monospace)}>
        <SettingWrapper id="max-price">
          <FormTextInput
            disabled={!canEditSettings || maxPriceDisabled}
            input={maxPriceInput}
            label="Max price"
            prefix={currencyDisplay}
            type="number"
          />
        </SettingWrapper>
        {maxPriceFooter}
      </div>
    </>
  );
});

export type CoverageMlModelMode =
  | { mode: "edit" }
  | { mode: "restricted"; reason: string };

export function getCoverageMlModelMode({
  email,
  restrictedReason,
}: {
  email: string | undefined;
  restrictedReason: string;
}): CoverageMlModelMode {
  return isAdminCheckoutOptimizationUser(email)
    ? { mode: "edit" as const }
    : { mode: "restricted" as const, reason: restrictedReason };
}

type IntentModelConfigInput = GroupInput<{
  minPriceBiasPercentOfConstraintRange: Input<string>;
  maxPriceBiasPercentOfConstraintRange: Input<string>;
}>;

export const PackageProtectionMlModelSettings = memo(
  function PackageProtectionMlModelSettings({
    coverageMlModelEnabled,
    coverageMlModelId,
    canEditSettings,
    coverageMlModelMode,
    minPriceInput,
    maxPriceInput,
    currencyDisplay,
    intentModelConfig,
    email,
    minPriceFooter,
    maxPriceFooter,
    maxPriceDisabled,
    stagecoachPriceVsConversionWeight,
    stagecoachThompsonSamplingEnabled,
  }: {
    coverageMlModelEnabled: Input<boolean>;
    coverageMlModelId: Input<CoverageMlModelId>;
    canEditSettings: boolean;
    coverageMlModelMode: CoverageMlModelMode;
    minPriceInput?: Input<string>;
    maxPriceInput?: Input<string>;
    minPriceFooter?: ReactNode;
    maxPriceFooter?: ReactNode;
    maxPriceDisabled?: boolean;
    currencyDisplay?: string;
    intentModelConfig?: IntentModelConfigInput;
    email?: string;
    stagecoachPriceVsConversionWeight?: Input<string>;
    stagecoachThompsonSamplingEnabled?: Input<boolean>;
  }) {
    const options = useMemo(() => {
      const base = [...baseMlModelOptions];
      if (isTrustedAdminCheckoutOptimizationUser(email)) {
        base.push({
          id: STAGECOACH_COVERAGE_ML_MODEL_ID,
          type: "text" as const,
          value: STAGECOACH_COVERAGE_ML_MODEL_ID,
          text: "Stagecoach (ML model)",
        });
      }
      return base;
    }, [email]);

    if (
      coverageMlModelMode.mode === "restricted" &&
      !coverageMlModelEnabled.value
    ) {
      return null;
    }

    return (
      <SettingSectionWrapper id="ml-model">
        <div className={classNames(gridCss.span12)}>
          <Flex dir="column" gap="xs" mb="md" w="full">
            <SettingWrapper compact id="use-ml-model">
              <RedoToggle
                description="Enable machine learning models to optimize package protection pricing"
                disabled={
                  !canEditSettings ||
                  (coverageMlModelMode.mode === "restricted" &&
                    coverageMlModelEnabled.value)
                    ? ((coverageMlModelMode.mode === "restricted" &&
                      coverageMlModelEnabled.value
                        ? coverageMlModelMode.reason
                        : undefined) ?? true)
                    : false
                }
                label="Use ML model for pricing"
                setValue={coverageMlModelEnabled.setValue}
                size="sm"
                value={coverageMlModelEnabled.value}
              />
            </SettingWrapper>
            {coverageMlModelEnabled.value && (
              <Flex dir="column" gap="xs" mt="sm">
                <DisabledTooltip
                  disabledMessage={
                    coverageMlModelMode.mode === "restricted" &&
                    coverageMlModelEnabled.value
                      ? coverageMlModelMode.reason
                      : undefined
                  }
                >
                  <SettingWrapper id="ml-model-select">
                    <RedoSingleSelectDropdownInput
                      description="Choose the machine learning model for price optimization"
                      disabled={
                        !canEditSettings ||
                        (coverageMlModelMode.mode === "restricted" &&
                          coverageMlModelEnabled.value)
                      }
                      fullWidth
                      label="ML model"
                      options={options}
                      optionSelected={({ item }) => {
                        coverageMlModelId.setValue(item.value);
                      }}
                      placeholder="Select ML model"
                      selectedItem={options.find(
                        (option) => option.value === coverageMlModelId.value,
                      )}
                      size="sm"
                    />
                  </SettingWrapper>
                </DisabledTooltip>
                {coverageMlModelId.value ===
                  INTENT_V12_28_2025_COVERAGE_ML_MODEL_ID &&
                  intentModelConfig && (
                    <div className={gridCss.grid}>
                      <div className={classNames(gridCss.span6)}>
                        <SettingWrapper id="min-price-bias">
                          <FormTextInput
                            description="Min bias % (-1 to 1)"
                            input={
                              intentModelConfig.inputs
                                .minPriceBiasPercentOfConstraintRange
                            }
                            label="Min price bias %"
                            labelTheme={LabelTheme.THIN}
                            placeholder="0"
                          />
                        </SettingWrapper>
                      </div>
                      <div className={classNames(gridCss.span6)}>
                        <SettingWrapper id="max-price-bias">
                          <FormTextInput
                            description="Max bias % (-1 to 1, default: 0.3)"
                            input={
                              intentModelConfig.inputs
                                .maxPriceBiasPercentOfConstraintRange
                            }
                            label="Max price bias %"
                            labelTheme={LabelTheme.THIN}
                            placeholder="0.3"
                          />
                        </SettingWrapper>
                      </div>
                    </div>
                  )}
                {coverageMlModelId.value === STAGECOACH_COVERAGE_ML_MODEL_ID &&
                  stagecoachPriceVsConversionWeight && (
                    <div className={gridCss.grid}>
                      <div className={classNames(gridCss.span6)}>
                        <SettingWrapper id="conversion-weight">
                          <FormTextInput
                            description="1.0 = maximize revenue, <1 favors conversion, >1 favors margin"
                            input={stagecoachPriceVsConversionWeight}
                            label="Price vs conversion weight (α)"
                            labelTheme={LabelTheme.THIN}
                            placeholder="1.0"
                          />
                        </SettingWrapper>
                      </div>
                      {stagecoachThompsonSamplingEnabled && (
                        <div className={classNames(gridCss.span6)}>
                          <SettingWrapper id="thompson-sampling">
                            <RedoToggle
                              description="Sample from posterior distribution instead of using posterior mean"
                              disabled={!canEditSettings}
                              label="Thompson sampling"
                              setValue={
                                stagecoachThompsonSamplingEnabled.setValue
                              }
                              size="sm"
                              value={stagecoachThompsonSamplingEnabled.value}
                            />
                          </SettingWrapper>
                        </div>
                      )}
                    </div>
                  )}
                {minPriceInput &&
                  maxPriceInput &&
                  coverageMlModelId.value !==
                    INTENT_V12_28_2025_COVERAGE_ML_MODEL_ID && (
                    <div className={gridCss.grid}>
                      <MinMaxPriceInput
                        canEditSettings={canEditSettings}
                        currencyDisplay={currencyDisplay}
                        maxPriceDisabled={maxPriceDisabled}
                        maxPriceFooter={maxPriceFooter}
                        maxPriceInput={maxPriceInput}
                        minPriceFooter={minPriceFooter}
                        minPriceInput={minPriceInput}
                      />
                    </div>
                  )}
              </Flex>
            )}
          </Flex>
        </div>
      </SettingSectionWrapper>
    );
  },
);

const baseMlModelOptions: TextRedoListItem<CoverageMlModelId>[] = [
  {
    id: INTENT_V12_28_2025_COVERAGE_ML_MODEL_ID,
    type: "text" as const,
    value: INTENT_V12_28_2025_COVERAGE_ML_MODEL_ID,
    text: "Intent v12_28_2025",
  },
];
